import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, NgZone, OnDestroy, Output, ViewChild,
} from '@angular/core';
import { PRACTICE_AI, PRACTICE_SCENARIO } from '../../models/scenario-practice-data';
import { Scenario, ScenarioTarget, StepType } from '../../models/scenario.model';
import { attachScenarioCoaching } from '../../services/scenario-coaching';
import { ScenarioBuilderPageComponent } from '../scenario-builder-page/scenario-builder-page.component';

const CANCELLED = Symbol('walkthrough cancelled');
// Demonstration pacing gives viewers time to follow the cursor and read the result of each action.
const MOVE_MS = 650;
/** Short hops move faster than long sweeps, capped at MOVE_MS. */
const MOVE_MIN_MS = 320;
/** Cursor paths bow sideways by this share of their length, up to ARC_MAX px, like a wrist sweeping a mouse. */
const ARC_BOW = 0.14;
const ARC_MAX = 56;
const ARC_SAMPLES = 30;
const HOLD_MS = 1400;
/** The focus hole glides only right after it retargets, then tracks layout exactly. */
const GLIDE_MS = 140;
/** Beat cards fade out in place and reappear in the next region; they never slide across the stage. */
const BEAT_FADE_MS = 160;
/** At or below the builder's compact breakpoint the card becomes a title strip at the top or bottom of the stage. */
const COMPACT_WIDTH = 800;

@Component({
  selector: 'app-scenario-walkthrough',
  templateUrl: './scenario-walkthrough.component.html',
  styleUrls: ['./scenario-walkthrough.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScenarioWalkthroughComponent implements AfterViewInit, OnDestroy {
  @Output() readonly closed = new EventEmitter<void>();
  @Output() readonly practice = new EventEmitter<void>();
  @ViewChild(ScenarioBuilderPageComponent) private editor: ScenarioBuilderPageComponent;
  @ViewChild('stage') private stage: ElementRef<HTMLElement>;
  @ViewChild('cursor') private cursor: ElementRef<HTMLElement>;
  @ViewChild('spotlight') private spotlight: ElementRef<HTMLElement>;
  @ViewChild('beatCard') private beatCard: ElementRef<HTMLElement>;
  @ViewChild('play') private play: ElementRef<HTMLButtonElement>;
  @ViewChild('finishDialog') private finishDialog: ElementRef<HTMLDialogElement>;
  @ViewChild('finishPrimary') private finishPrimary: ElementRef<HTMLButtonElement>;

  @ViewChild('scrub') private scrub: ElementRef<HTMLInputElement>;

  readonly chapters = ['Scene', 'Question', 'Help & debrief', 'Connect paths', 'Preview', 'Save & reopen'];
  /**
   * Each step is one demonstrated beat: its caption, then the actions it narrates. Seeking snaps to step starts; the
   * editor is rebuilt from the chapter checkpoint plus the chapter's earlier steps, so any step is reachable cold.
   */
  private readonly steps: WalkthroughStep[] = [
    this.step(0, 'add', 'Add the first step', 'Every lesson opens with a Narrative step that sets the scene.', async r => {
      await this.click('[data-tour="add-step"]', r);
      await this.click('[data-tour-add="narrative"]', r);
    }),
    this.step(0, 'inspector', 'Write the briefing', 'Name the step, then describe the situation students fly into.', async r => {
      await this.type('[data-tour="step-title"]', 'Briefing', r);
      await this.type('[data-tour="body"]', PRACTICE_SCENARIO.steps[0].body, r);
    }),
    this.step(0, 'toolbar', 'Name the scenario', 'Give the flow a title, then fit the canvas to review it.', async r => {
      await this.type('[data-tour="flow-title"]', PRACTICE_SCENARIO.title, r);
      await this.closeSettings(r);
    }),
    this.step(1, 'canvas', 'Add a question', 'Select the briefing, then open the question library from Add step.', async r => {
      await this.select('narrative', r);
      await this.closeSettings(r);
    }),
    this.step(1, 'add', 'Browse question types', 'Open the library to build a reusable question.', async r => {
      await this.click('[data-tour="add-step"]', r);
      await this.click('[data-tour="browse-types"]', r);
    }),
    this.step(1, 'types', 'Pick a question type', 'Multiple choice suits a single go/no-go fuel decision.', async r => {
      if (this.visible('[data-tour="types-pane"]')) { await this.click('[data-tour="types-pane"]', r); }
      await this.click('[data-tour-kind="multiple-choice"]', r);
    }),
    // AI beats drive the real Draft with AI controls; the demonstration editor answers from prepared data, never the network.
    this.step(1, 'library', 'AI knows the course', 'Drafts target the course and module chosen when the flow was started.',
      r => this.point('[data-tour="ai-target"]', r)),
    this.step(1, 'library', 'ACS codes come ready', 'The course\'s most-missed ACS codes are preselected. Use Choose ACS codes to change them.',
      r => this.point('[data-tour="ai-acs"] .btn', r)),
    this.step(1, 'library', 'Add a learning goal', 'An optional topic focuses the draft on one idea.',
      r => this.type('[data-tour="ai-topic"]', PRACTICE_AI.topic, r)),
    this.step(1, 'library', 'Draft the question', 'AI writes the prompt, choices, answer key, and feedback. Nothing is saved yet.',
      r => this.draft(r)),
    this.step(1, 'library', 'Try another version', 'Not quite right? Ask for a fresh take on the same target.',
      r => this.draft(r)),
    this.step(1, 'library', 'Review the draft', 'Check the answer key and feedback, and edit anything before you save.', async r => {
      await this.point('.sb-lib-choice.is-correct', r);
      await this.point('[data-tour="explanation"]', r);
    }),
    this.step(1, 'library', 'Save and add it', 'Saved questions are reusable; Add to flow places this one next.', async r => {
      await this.click('[data-tour="save-question"]', r);
      await this.click('[data-tour="add-question"]', r);
    }),
    this.step(1, 'canvas', 'Back on the canvas', 'The question now follows the briefing in your flow.', r => this.closeSettings(r)),
    this.step(2, 'canvas', 'Add coaching', 'Attach optional coaching to a question after a missed answer.', async r => {
      await this.select('question', r);
      await this.closeSettings(r);
    }),
    this.step(2, 'canvas', 'Open the coaching footer', 'Coaching stays inside the question, without another card.', async r => {
      await this.click('[data-tour="coaching"]', r);
    }),
    this.step(2, 'canvas', 'Explain the mistake', 'Point students back to the fuel math they skipped. Retry question is selected by default.', async r => {
      await this.type('[data-tour="coaching-text"]', PRACTICE_SCENARIO.steps[2].body, r);
      await this.click('[data-tour="coaching-done"]', r);
      await this.closeSettings(r);
    }),
    this.step(2, 'add', 'Add the debrief', 'A Debrief closes the lesson with the key takeaway.', async r => {
      await this.click('[data-tour="add-step"]', r);
      await this.click('[data-tour-add="debrief"]', r);
    }),
    this.step(2, 'inspector', 'Write the takeaway', 'Summarize what a sound fuel decision looks like.', async r => {
      await this.type('[data-tour="step-title"]', 'Debrief', r);
      await this.type('[data-tour="body"]', PRACTICE_SCENARIO.steps[3].body, r);
      await this.closeSettings(r);
    }),
    // All four connect beats share the canvas zone, so the card stays clear of every node they connect.
    this.step(3, 'canvas', 'Route correct answers', 'Drag the correct port onto the Debrief step.', async r => {
      await this.click(FIT, r);
      await this.drag('question', 'next', 'debrief', r);
    }),
    this.step(3, 'canvas', 'Review missed answers', 'The coaching footer handles incorrect answers inside the question.',
      r => this.point('[data-tour="coaching"]', r)),
    this.step(3, 'canvas', 'Retry without a loop', 'The Retry badge means students answer this question again, without a dashed path.',
      r => this.point('.sb-coaching__badge', r)),
    this.step(3, 'canvas', 'Check the routes', 'Select the question; its Paths tab lists every destination.', async r => {
      await this.select('question', r);
      await this.click('[data-tour-tab="paths"]', r);
      await this.wait(2200, r);
      await this.closeSettings(r);
    }),
    this.step(4, 'toolbar', 'Preview as a student', 'Run the lesson exactly as students will see it.', async r => {
      await this.click('[data-tour="preview"]', r);
      await this.click('.sp-card__footer .btn-primary', r);
    }),
    this.step(4, 'preview', 'Test a wrong answer', 'Choose the wrong option on purpose to check the help path.', async r => {
      await this.click('.sq-option:nth-child(2)', r);
      await this.click('.sq-footer__btn', r);
    }),
    this.step(4, 'preview', 'Coaching steps in', 'The note appears below the answer. Try again resets the same question.', async r => {
      await this.wait(1800, r);
      await this.click('.sq-footer__btn', r);
    }),
    this.step(4, 'preview', 'Answer correctly', 'The right answer routes to the Debrief and completes the lesson.', async r => {
      await this.click('.sq-option:first-child', r);
      await this.click('.sq-footer__btn', r);
      await this.click('.sq-footer__btn', r);
      await this.click('.sp-card__footer .btn-primary', r);
      await this.click('.sp-results .btn-secondary', r);
    }),
    this.step(5, 'toolbar', 'Save the flow', 'Save from the Flow menu, then go back to all flows.', async r => {
      await this.click('[data-tour="flow-menu"]', r);
      await this.click('[data-tour="save-flow"]', r);
      await this.click('[data-tour="all-flows"]', r);
    }),
    this.step(5, 'flows', 'Reopen your work', 'Saved flows keep every step, path, and question together.',
      r => this.click('[data-tour="saved-flow"]', r)),
    this.step(5, 'canvas', 'Your turn', 'Try this example in the editor, or replay any chapter.', r => this.click(FIT, r)),
  ];
  readonly lastStep = this.steps.length - 1;
  private readonly chapterStarts = this.chapters.map((_, chapter) => this.steps.findIndex(step => step.chapter === chapter));
  /** Chapter spans on the scrub rail; each step owns an equal slice centred on its thumb stop. */
  readonly segments: ChapterSegment[] = this.chapters.map((label, chapter) => {
    const first = this.chapterStarts[chapter], end = this.chapterStarts[chapter + 1] ?? this.steps.length;
    const left = this.railFraction(first - 0.5), right = this.railFraction(end - 0.5);
    return { label, first, cut: first ? percent(left) : '', width: percent(right - left) };
  });
  readonly ticks = this.steps.map((_, index) => percent(this.railFraction(index)));
  chapter = 0;
  /** Committed playback step; the editor shows its starting state while the step's caption is read. */
  stepIndex = 0;
  /** Thumb value: follows a drag preview without touching the editor until the drag is released. */
  scrubIndex = 0;
  scrubbing = false;
  hovering = false;
  atFirstStep = true;
  atLastStep = false;
  fillWidth = '0%';
  scrubText = '';
  tipLeft = '0%';
  tipShift = 'translateX(0%)';
  tipMeta = '';
  tipTitle = '';
  beatTitle = '';
  beatBody = '';
  beatHidden = true;
  playing = false;
  finished = false;
  error = '';
  clicking = false;
  cursorVisible = false;
  private revision = 0;
  /** While true, steps rebuild the editor synchronously: no pauses, pointer travel, typing, or captions. */
  private instant = false;
  /** The rebuild ended on a canvas fit, which must be redone once the editor has measured its new nodes. */
  private refit = false;
  private hoverIndex = -1;
  private resumeAfterScrub = false;
  private finishTrigger: HTMLElement | null = null;
  private animation: Animation | null = null;
  private x = 30;
  private y = 30;
  private focusSelectors: string[] = [];
  private focusFrame = 0;
  private retarget = false;
  private hole: FocusBox | null = null;
  private glide: { from: FocusBox; at: number } | null = null;
  private maskValue = '';
  private beatZone: BeatZone | null = null;
  /** Placement key kept for the whole beat so the card only moves when the layout really changes. */
  private beatSide = '';
  private beatPlacement = '';
  private readonly reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  constructor(private readonly cdr: ChangeDetectorRef, private readonly zone: NgZone) {
    this.syncScrub();
  }

  ngAfterViewInit(): void {
    // Start after the modal has opened and its editor has measured the canvas.
    setTimeout(() => { if (this.revision === 0) { this.seekStep(0, true); this.play.nativeElement.focus(); } });
  }

  ngOnDestroy(): void {
    this.endScrubListeners();
    this.stop();
    this.closeFinish(false);
  }

  closeFinish(restoreFocus = true): void {
    const trigger = this.finishTrigger;
    this.finishTrigger = null;
    if (this.finishDialog?.nativeElement.open) { this.finishDialog.nativeElement.close(); }
    if (restoreFocus) { (trigger?.isConnected && trigger !== document.body ? trigger : this.play.nativeElement).focus(); }
  }

  cancelFinish(event: Event): void {
    // Escape dismisses only the finish dialog; the outer walkthrough dialog stays open.
    event.preventDefault();
    event.stopPropagation();
    this.closeFinish();
  }

  finishClosed(): void {
    // Browsers may close without a cancellable cancel event; still return focus to the player.
    if (this.finishTrigger) { this.closeFinish(); }
  }

  practiceFromFinish(): void {
    this.closeFinish(false);
    this.practice.emit();
  }

  toggle(): void {
    if (this.finished) { this.seekStep(0, true); return; }
    // A stopped step retries from its own start rather than restarting the whole walkthrough.
    if (this.error) { this.seekStep(this.stepIndex, true); return; }
    this.setPlaying(!this.playing);
  }

  replay(): void {
    this.seekStep(0, true);
  }

  seek(chapter: number): void {
    this.seekStep(this.chapterStarts[Math.max(0, Math.min(this.chapters.length - 1, chapter))]);
  }

  /**
   * Cancels everything in flight, restores the editor to the start of `index`, and keeps the current play/pause state
   * unless `play` says otherwise. Focus stays where it was unless its control closed or became disabled.
   */
  seekStep(index: number, play = this.playing): void {
    const finishFocused = !!this.finishDialog?.nativeElement.contains(document.activeElement);
    this.stop();
    this.closeFinish(false);
    const target = Math.max(0, Math.min(this.lastStep, index));
    this.finished = false;
    this.error = '';
    this.cursorVisible = false;
    this.beatHidden = true;
    this.beatZone = null;
    this.setStep(target);
    this.editor.resetDemonstration(checkpoint(this.chapter));
    this.playing = play;
    const active = document.activeElement as HTMLButtonElement | null;
    this.render();
    if (finishFocused || active?.disabled) { this.play.nativeElement.focus(); }
    const revision = this.revision;
    this.run(target, revision).catch(error => {
      if (error === CANCELLED || revision !== this.revision) { return; }
      this.instant = false;
      this.error = 'The walkthrough stopped. Press Play to retry this step.';
      this.playing = false;
      this.clearFocus();
      this.cursorVisible = false;
      this.beatTitle = 'Walkthrough stopped';
      this.beatBody = this.error;
      this.beatHidden = false;
      this.render();
      this.placeBeat();
    });
  }

  onKey(event: KeyboardEvent): void {
    if ((event.target as HTMLElement).closest('input, textarea')) { return; }
    if (event.key === 'ArrowLeft') { event.preventDefault(); this.seekStep(this.stepIndex - 1); }
    else if (event.key === 'ArrowRight') { event.preventDefault(); this.seekStep(this.stepIndex + 1); }
    else if (event.key.toLowerCase() === 'r') { event.preventDefault(); this.replay(); }
    // Buttons keep their native Space behavior; the player surface supports Space too.
    else if (event.code === 'Space' && !(event.target as HTMLElement).closest('button')) { event.preventDefault(); this.toggle(); }
  }

  // ---------- scrubbing: pointer drags preview a step and seek once on release; keyboard changes seek at once ----------

  startScrub(event: PointerEvent): void {
    if (event.button !== 0 || this.scrubbing) { return; }
    this.scrubbing = true;
    this.resumeAfterScrub = this.playing;
    this.setPlaying(false);
    // Native ranges don't reliably fire `change` for a press that ends where it started, so release is tracked here.
    window.addEventListener('pointerup', this.endScrub);
    window.addEventListener('pointercancel', this.endScrub);
    this.syncScrub();
  }

  previewScrub(value: number): void {
    this.scrubIndex = value;
    this.syncScrub();
  }

  commitScrub(value: number): void {
    // A pointer drag commits on release; a `change` arriving after that release targets the step already shown.
    if (this.scrubbing || value === this.stepIndex) { return; }
    this.seekStep(value);
  }

  hoverScrub(event: PointerEvent): void {
    const input = this.scrub?.nativeElement;
    if (event.pointerType !== 'mouse' || this.scrubbing || !input) { return; }
    const rect = input.getBoundingClientRect();
    const fraction = (event.clientX - rect.left - SCRUB_THUMB / 2) / Math.max(1, rect.width - SCRUB_THUMB);
    const index = Math.max(0, Math.min(this.lastStep, Math.round(fraction * this.lastStep)));
    if (this.hovering && index === this.hoverIndex) { return; }
    this.hovering = true;
    this.hoverIndex = index;
    this.syncScrub();
  }

  leaveScrub(): void {
    if (!this.hovering) { return; }
    this.hovering = false;
    this.hoverIndex = -1;
    this.syncScrub();
  }

  private readonly endScrub = (): void => {
    this.endScrubListeners();
    if (!this.scrubbing) { return; }
    this.scrubbing = false;
    if (this.scrubIndex !== this.stepIndex) { this.seekStep(this.scrubIndex, this.resumeAfterScrub); return; }
    this.setPlaying(this.resumeAfterScrub);
    this.syncScrub();
    this.render();
  }

  private endScrubListeners(): void {
    window.removeEventListener('pointerup', this.endScrub);
    window.removeEventListener('pointercancel', this.endScrub);
  }

  private setPlaying(playing: boolean): void {
    this.playing = playing;
    if (playing) { this.animation?.play(); } else { this.animation?.pause(); }
  }

  private setStep(index: number): void {
    this.stepIndex = index;
    this.chapter = this.steps[index].chapter;
    if (!this.scrubbing) { this.scrubIndex = index; }
    this.syncScrub();
  }

  /** Refreshes the cached scrubber view model: fill, boundary flags, and the hovered or dragged step's label. */
  private syncScrub(): void {
    const stop = this.hovering && !this.scrubbing ? this.hoverIndex : this.scrubIndex;
    const step = this.steps[stop], tip = this.railFraction(stop) * 100, total = this.steps.length;
    this.atFirstStep = this.stepIndex === 0;
    this.atLastStep = this.stepIndex === this.lastStep;
    this.fillWidth = percent(this.railFraction(this.scrubIndex));
    this.tipLeft = `${tip}%`;
    // Anchored at its left edge at the start of the rail and its right edge at the end, so it never overflows.
    this.tipShift = `translateX(-${tip}%)`;
    this.tipMeta = `Step ${stop + 1} of ${total} · ${this.chapters[step.chapter]}`;
    this.tipTitle = step.title;
    const current = this.steps[this.scrubIndex];
    this.scrubText = `Step ${this.scrubIndex + 1} of ${total}, ${this.chapters[current.chapter]}: ${current.title}`;
  }

  private railFraction(index: number): number {
    return Math.max(0, Math.min(1, index / this.lastStep));
  }

  private step(chapter: number, zone: BeatZone, title: string, body: string, act: (revision: number) => Promise<void>): WalkthroughStep {
    return { chapter, zone, title, body, act };
  }

  private stop(): void {
    this.revision++;
    this.playing = false;
    this.instant = false;
    this.animation?.cancel();
    this.animation = null;
    this.clearFocus();
    this.clicking = false;
  }

  private async run(from: number, revision: number): Promise<void> {
    await this.rebuild(from, revision);
    for (let index = from; index < this.steps.length; index++) {
      const step = this.steps[index];
      if (index === from) {
        // Let the restored editor register before the caption; a paused seek shows the caption right away.
        if (this.playing) { await this.wait(600, revision); }
      } else if (step.chapter !== this.steps[index - 1].chapter) {
        await this.wait(2200, revision);
        this.setStep(index);
        this.render();
        await this.wait(600, revision);
        // Place the same steps in a readable branch layout before demonstrating the connections.
        if (step.chapter === 3) { this.editor.resetDemonstration(checkpoint(3)); this.render(); }
      }
      this.guard(revision);
      this.setStep(index);
      await this.beat(step.zone, step.title, step.body, revision);
      await step.act(revision);
    }
    await this.wait(2200, revision);
    this.finished = true;
    this.playing = false;
    this.cursorVisible = false;
    this.clearFocus();
    this.render();
    this.openFinish();
  }

  /**
   * Rebuilds the editor as it stands just before step `to`: the chapter checkpoint is already applied, so replay the
   * chapter's earlier steps in instant mode. Instant waits are microtasks, so no frame paints mid-rebuild.
   */
  private async rebuild(to: number, revision: number): Promise<void> {
    const from = this.chapterStarts[this.steps[to].chapter];
    if (from === to) { return; }
    this.instant = true;
    this.refit = false;
    for (let index = from; index < to; index++) {
      this.guard(revision);
      await this.steps[index].act(revision);
    }
    this.guard(revision);
    this.instant = false;
    this.clearFocus();
    this.clicking = false;
    this.cursorVisible = false;
    this.render();
    if (this.refit) {
      // The editor measures new nodes in its own already-queued frame; fit again with real heights before that frame paints.
      await new Promise(resolve => requestAnimationFrame(resolve));
      this.guard(revision);
      this.editor.zoomFit();
      this.render();
    }
  }

  private guard(revision: number): void {
    if (revision !== this.revision) { throw CANCELLED; }
  }

  private openFinish(): void {
    const dialog = this.finishDialog.nativeElement;
    if (dialog.open) { return; }
    this.finishTrigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog.showModal();
    this.finishPrimary.nativeElement.focus();
  }

  private async select(type: StepType, revision: number): Promise<void> {
    const step = this.editor.scenario.steps.find(item => item.type === type);
    await this.click(`[data-tour-node="${step.id}"]`, revision, true);
    if (this.visible('[data-tour-tab="content"]')) { await this.click('[data-tour-tab="content"]', revision); }
  }

  private async closeSettings(revision: number): Promise<void> {
    const target = this.visible('[data-tour="done"]') ? '[data-tour="done"]' : '[data-tour="close-settings"]';
    if (this.visible(target)) { await this.click(target, revision); }
    await this.click(FIT, revision);
  }

  private async click(selector: string, revision: number, node = false): Promise<void> {
    this.refit = selector === FIT;
    this.focus(selector);
    let target = this.element(selector);
    await this.move(target, revision);
    if (!target.isConnected) { target = this.element(selector); await this.move(target, revision); }
    this.clicking = true;
    this.render();
    await this.wait(150, revision);
    if (node) { target.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true })); }
    else { target.click(); }
    this.clicking = false;
    this.render();
    await this.wait(HOLD_MS, revision);
  }

  /** Clicks Draft, holds on the drafting dither and skeleton (click's own hold), then lets the prepared draft land. */
  private async draft(revision: number): Promise<void> {
    await this.click('[data-tour="ai-draft"]', revision);
    this.editor.finishDemonstrationDraft();
    this.render();
    await this.wait(HOLD_MS, revision);
  }

  /** Points at a control so its result can be read, without clicking it. */
  private async point(selector: string, revision: number): Promise<void> {
    this.focus(selector);
    await this.move(this.element(selector), revision);
    await this.wait(HOLD_MS, revision);
  }

  private async type(selector: string, value: string, revision: number): Promise<void> {
    this.refit = false;
    this.focus(selector);
    let target = this.element(selector) as HTMLInputElement | HTMLTextAreaElement;
    await this.move(target, revision);
    // Hide the pointer while typing so it never covers the text being entered.
    this.cursorVisible = false;
    this.render();
    const length = this.reduced || this.instant ? 0 : Math.min(value.length, 42);
    for (let i = 0; i <= length; i++) {
      if (!target.isConnected) { target = this.element(selector) as HTMLInputElement | HTMLTextAreaElement; }
      target.value = value.slice(0, i);
      target.dispatchEvent(new Event('input', { bubbles: true }));
      this.render();
      await this.wait(45, revision);
    }
    target.value = value;
    target.dispatchEvent(new Event('input', { bubbles: true }));
    this.render();
    await this.wait(HOLD_MS, revision);
  }

  private async drag(fromType: StepType, port: string, toType: StepType, revision: number): Promise<void> {
    const from = this.editor.scenario.steps.find(step => step.type === fromType);
    const to = this.editor.scenario.steps.find(step => step.type === toType);
    this.refit = false;
    const outputSelector = `[data-tour-node="${from.id}"] [data-port="${port}"] .sb-port`;
    const inputSelector = `[data-tour-node="${to.id}"] .sb-port-in`;
    // Keep both ends (and the wire between them) in focus for the whole gesture.
    this.focus(`[data-tour-node="${from.id}"]`, `[data-tour-node="${to.id}"]`);
    const output = this.element(outputSelector);
    const input = this.element(inputSelector);
    await this.move(output, revision);
    const start = output.getBoundingClientRect(), end = input.getBoundingClientRect();
    const sx = start.left + start.width / 2, sy = start.top + start.height / 2;
    const ex = end.left + end.width / 2 + 3, ey = end.top + end.height / 2;
    const send = (name: string, x: number, y: number): void => {
      output.dispatchEvent(new PointerEvent(name, { bubbles: true, pointerId: 1, button: 0, buttons: name === 'pointerup' ? 0 : 1,
        clientX: x, clientY: y, pointerType: 'mouse' }));
      this.render();
    };
    send('pointerdown', sx, sy);
    const frames = this.reduced || this.instant ? 1 : 36;
    for (let frame = 1; frame <= frames; frame++) {
      const { x, y } = arcPoint({ x: sx, y: sy }, { x: ex, y: ey }, frame / frames);
      this.placeCursor(x, y);
      send('pointermove', x, y);
      await this.wait(this.reduced ? 0 : 32, revision);
    }
    send('pointerup', ex, ey);
    await this.wait(1800, revision);
  }

  private element(selector: string): HTMLElement {
    const element = this.stage.nativeElement.querySelector<HTMLElement>(selector);
    if (!element) { throw new Error(`Missing walkthrough target: ${selector}`); }
    element.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    return element;
  }

  private visible(selector: string): boolean {
    return !!this.stage.nativeElement.querySelector<HTMLElement>(selector)?.getClientRects().length;
  }

  private async move(target: HTMLElement, revision: number): Promise<void> {
    const rect = target.getBoundingClientRect(), stage = this.stage.nativeElement.getBoundingClientRect();
    const x = rect.left + rect.width / 2 - stage.left, y = rect.top + rect.height / 2 - stage.top;
    this.cursorVisible = true;
    this.render();
    if (!this.reduced && !this.instant) {
      const from = { x: this.x, y: this.y }, to = { x, y };
      const duration = Math.round(Math.min(MOVE_MS, MOVE_MIN_MS + Math.hypot(x - from.x, y - from.y) * 0.6));
      // Sampled arc keyframes keep pause/resume on the Web Animation while the path curves and settles softly.
      const keyframes = Array.from({ length: ARC_SAMPLES + 1 }, (_, i) => {
        const point = arcPoint(from, to, i / ARC_SAMPLES);
        return { transform: `translate(${point.x}px, ${point.y}px)` };
      });
      this.animation = this.cursor.nativeElement.animate(keyframes, { duration, easing: 'linear', fill: 'forwards' });
      await this.wait(duration, revision);
    }
    this.x = x; this.y = y;
    this.cursor.nativeElement.style.transform = `translate(${x}px, ${y}px)`;
    this.animation?.cancel();
    this.animation = null;
    await this.wait(180, revision);
  }

  private placeCursor(clientX: number, clientY: number): void {
    const stage = this.stage.nativeElement.getBoundingClientRect();
    this.x = clientX - stage.left; this.y = clientY - stage.top;
    this.cursor.nativeElement.style.transform = `translate(${this.x}px, ${this.y}px)`;
  }

  /** Keeps the next action and its surrounding context clear, following re-rendered DOM. */
  private focus(...selectors: string[]): void {
    this.focusSelectors = selectors;
    this.retarget = true;
    this.track();
  }

  private track(): void {
    if (!this.focusFrame) { this.zone.runOutsideAngular(() => { this.focusFrame = requestAnimationFrame(this.trackFocus); }); }
  }

  private clearFocus(): void {
    cancelAnimationFrame(this.focusFrame);
    this.focusFrame = 0;
    this.focusSelectors = [];
    this.hole = null;
    this.glide = null;
    this.spotlight?.nativeElement.classList.remove('is-on');
  }

  // Track layout outside Angular so canvas motion and replaced nodes don't trigger change detection every frame.
  private readonly trackFocus = (now: number): void => {
    this.focusFrame = requestAnimationFrame(this.trackFocus);
    this.placeBeat();
    const stage = this.stage.nativeElement, spotlight = this.spotlight.nativeElement, bounds = stage.getBoundingClientRect();
    const rects = focusRects(stage, this.focusSelectors);
    const goal = focusBox(rects, bounds);
    if (!goal) {
      // The target unmounted or hid (e.g. a clicked menu closed): drop the hole now instead of framing empty space.
      this.hole = this.glide = null;
      spotlight.classList.remove('is-on');
      return;
    }
    if (this.retarget) {
      this.retarget = false;
      this.glide = this.hole && !this.reduced ? { from: this.hole, at: now } : null;
    }
    let box = goal;
    if (this.glide) {
      const t = Math.min(1, (now - this.glide.at) / GLIDE_MS), e = 1 - Math.pow(1 - t, 3);
      const from = this.glide.from;
      box = { left: from.left + (goal.left - from.left) * e, top: from.top + (goal.top - from.top) * e,
        right: from.right + (goal.right - from.right) * e, bottom: from.bottom + (goal.bottom - from.bottom) * e };
      if (t === 1) { this.glide = null; }
    }
    this.hole = box;
    const mask = focusMask(box, bounds.width, bounds.height);
    if (mask !== this.maskValue) {
      spotlight.style.setProperty('-webkit-mask-image', mask);
      spotlight.style.setProperty('mask-image', mask);
      this.maskValue = mask;
    }
    spotlight.classList.add('is-on');
  }

  /** Shows the next beat's card beside its region, then pauses so it can be read before the cursor moves. */
  private async beat(zone: BeatZone, title: string, body: string, revision: number): Promise<void> {
    if (zone !== this.beatZone) {
      if (!this.beatHidden) { this.beatHidden = true; this.render(); await this.wait(BEAT_FADE_MS, revision); }
      this.beatZone = zone;
      this.beatSide = '';
    }
    this.beatTitle = title;
    this.beatBody = body;
    this.beatHidden = false;
    this.cursorVisible = false;
    this.render();
    this.placeBeat();
    this.focus(BEAT_ZONES[zone].anchor);
    await this.wait(readMs(title, body), revision);
  }

  /** Runs every tracked frame; writes the card position only when the zone's geometry gives a new spot. */
  private placeBeat(): void {
    const card = this.beatCard?.nativeElement, stage = this.stage?.nativeElement;
    if (!card || !stage) { return; }
    const bounds = stage.getBoundingClientRect(), width = bounds.width, height = bounds.height;
    const zone = this.beatZone && BEAT_ZONES[this.beatZone];
    const anchorRects = zone ? this.rects(zone.anchor) : [], areaRects = zone?.area ? this.rects(zone.area) : [];
    // Hold the last spot through view switches, but still clamp it when the viewport changes.
    const regionMissing = zone && !(zone.area ? areaRects.length : anchorRects.length);
    const compact = width <= COMPACT_WIDTH;
    if (card.classList.contains('is-compact') !== compact) { card.classList.toggle('is-compact', compact); this.beatSide = ''; }
    const size = { width: card.offsetWidth, height: card.offsetHeight };
    const anchor = focusBox(anchorRects, bounds);
    let left = BEAT_MARGIN, top = BEAT_MARGIN;
    if (compact) {
      // Phones: a title strip on the far side of the stage from the active region.
      if (!this.beatSide) { this.beatSide = anchor && (anchor.top + anchor.bottom) / 2 < height / 2 ? 'bottom' : 'top'; }
      if (this.beatSide === 'bottom') {
        const toolbar = focusBox(this.rects('.sb-canvas-toolbar'), bounds, 0);
        const bottom = toolbar ? toolbar.top : height;
        top = bottom - size.height - BEAT_MARGIN;
      }
    } else if (regionMissing && this.beatPlacement) {
      [left, top] = this.beatPlacement.split(',').map(Number);
    } else {
      const area = focusBox(areaRects, bounds, 0) || { left: 0, top: 0, right: width, bottom: height };
      const avoid = zone?.avoid ? this.rects(zone.avoid).map(rect => focusBox([rect], bounds)) : [];
      const spot = placeCard(size, area, anchor, avoid, zone?.sides || [], this.beatSide);
      this.beatSide = spot.key;
      left = spot.left; top = spot.top;
    }
    // Short stages: never let the card fall off the bottom or right edge.
    left = Math.round(Math.max(BEAT_MARGIN, Math.min(left, width - size.width - BEAT_MARGIN)));
    top = Math.round(Math.max(BEAT_MARGIN, Math.min(top, height - size.height - BEAT_MARGIN)));
    const placement = `${left},${top}`;
    if (placement === this.beatPlacement) { return; }
    this.beatPlacement = placement;
    card.style.transform = `translate(${left}px, ${top}px)`;
  }

  private rects(selector: string): DOMRect[] {
    return Array.from(this.stage.nativeElement.querySelectorAll<HTMLElement>(selector))
      .filter(element => element.getClientRects().length && getComputedStyle(element).visibility === 'visible')
      .map(element => element.getBoundingClientRect());
  }

  private render(): void {
    this.cdr.detectChanges();
  }

  private async wait(ms: number, revision: number): Promise<void> {
    if (this.instant) {
      // One microtask still lets pending form writes settle in order, without giving the browser a frame to paint.
      this.guard(revision);
      await Promise.resolve();
      this.guard(revision);
      return;
    }
    let remaining = ms;
    while (remaining > 0 || !this.playing) {
      if (revision !== this.revision) { throw CANCELLED; }
      const start = Date.now();
      await new Promise(resolve => setTimeout(resolve, Math.min(50, Math.max(1, remaining))));
      if (this.playing) { remaining -= Date.now() - start; }
    }
    if (revision !== this.revision) { throw CANCELLED; }
  }
}

interface FocusBox { left: number; top: number; right: number; bottom: number; }
interface WalkthroughStep {
  chapter: number; zone: BeatZone; title: string; body: string;
  /** The actions this step narrates; the editor state before them is what a seek to this step restores. */
  act: (revision: number) => Promise<void>;
}
interface ChapterSegment { label: string; first: number; cut: string; width: string; }

const FIT = '[data-tour="fit"]';
/** Matches the scrub thumb width in the stylesheet; the native thumb travels inside half a thumb of each end. */
const SCRUB_THUMB = 16;
const percent = (fraction: number): string => `${Math.round(fraction * 10000) / 100}%`;
interface Point { x: number; y: number; }

/** Point at progress t (0–1, eased here) on a gently bowed path; the bow lifts up the screen, or sideways on vertical moves. */
export function arcPoint(from: Point, to: Point, t: number): Point {
  // Cubic ease-in-out on slightly front-loaded time: a quick start and a long, soft settle on the target.
  const w = Math.pow(Math.min(1, Math.max(0, t)), 0.8);
  const e = w < 0.5 ? 4 * w * w * w : 1 - Math.pow(2 - 2 * w, 3) / 2;
  const dx = to.x - from.x, dy = to.y - from.y, distance = Math.hypot(dx, dy);
  const bow = distance ? Math.min(distance * ARC_BOW, ARC_MAX) / distance * (dx >= 0 ? -1 : 1) : 0;
  const cx = (from.x + to.x) / 2 - dy * bow, cy = (from.y + to.y) / 2 + dx * bow;
  const a = (1 - e) * (1 - e), b = 2 * (1 - e) * e, c = e * e;
  return { x: a * from.x + b * cx + c * to.x, y: a * from.y + b * cy + c * to.y };
}

/** Expand visible targets to their useful context without keeping holes for hidden or removed controls. */
export function focusRects(stage: HTMLElement, selectors: string[]): DOMRect[] {
  const contexts = new Set<HTMLElement>();
  const visible = (element: HTMLElement) => element.getClientRects().length && getComputedStyle(element).visibility === 'visible';
  selectors.forEach(selector => Array.from(stage.querySelectorAll<HTMLElement>(selector)).forEach(target => {
    if (!visible(target)) { return; }
    const context = target.closest<HTMLElement>(
      '.sb-settings, .sb-lib-work, .sb-lib-rail, .sb-flow-preview, .sb-presets, .sb-add, '
      + '.sb-flows__item, [data-tour-node], .sb-top__titles, .sb-top__actions'
    ) || target;
    if (target.matches('[data-tour="fit"]')) {
      contexts.add(target.closest<HTMLElement>('.sb-canvas-wrap') || context);
    } else {
      contexts.add(context);
      // Absolutely positioned menus extend beyond the trigger's layout box.
      context.querySelectorAll<HTMLElement>('.sb-add__menu, .sb-presets__menu').forEach(menu => {
        if (visible(menu)) { contexts.add(menu); }
      });
    }
  }));
  return Array.from(contexts).filter(visible).map(context => context.getBoundingClientRect());
}

const FOCUS_PAD = 6;
/** Matches --radius-lg; the SVG mask cannot read custom properties. */
const FOCUS_RADIUS = 8;
/** Gaussian sigma; the dim ramps in over ~4σ (16px) starting at the padded edge, so the target itself stays crisp. */
const FEATHER = 4;

/** Padded union of the focus rects in stage space; one hole keeps a drag's wire visible. */
export function focusBox(rects: FocusBox[], stage: { left: number; top: number }, pad = FOCUS_PAD): FocusBox | null {
  if (!rects.length) { return null; }
  return {
    left: Math.min(...rects.map(rect => rect.left)) - stage.left - pad,
    top: Math.min(...rects.map(rect => rect.top)) - stage.top - pad,
    right: Math.max(...rects.map(rect => rect.right)) - stage.left + pad,
    bottom: Math.max(...rects.map(rect => rect.bottom)) - stage.top + pad,
  };
}

type BeatSide = 'left' | 'right' | 'above' | 'below';
type BeatZone = 'toolbar' | 'add' | 'canvas' | 'inspector' | 'types' | 'library' | 'preview' | 'flows';
const BEAT_MARGIN = 12;
/** Each beat card sits beside its zone's anchor (inside `area` when set), clear of the anchor and `avoid`. */
const BEAT_ZONES: Record<BeatZone, { anchor: string; area?: string; avoid?: string; sides: BeatSide[] }> = {
  toolbar: { anchor: '.sb-top', avoid: '[data-tour-node], .sb-settings, .sb-flow-preview, #sb-flow-menu', sides: ['below'] },
  add: { anchor: '.sb-add', area: '.sb-canvas-wrap', avoid: '[data-tour-node], .sb-zoom, .sb-add__menu', sides: ['left', 'above'] },
  canvas: { anchor: '[data-tour-node]', area: '.sb-canvas-wrap', avoid: '.sb-canvas-toolbar, .sb-add__menu, .sb-flow-preview',
    sides: ['right', 'left', 'below', 'above'] },
  inspector: { anchor: '.sb-settings', sides: ['left', 'above'] },
  types: { anchor: '.sb-lib-rail', sides: ['right'] },
  library: { anchor: '.sb-lib-work__head, .sb-lib-pane--edit', sides: ['left', 'right'] },
  preview: { anchor: '.sb-flow-preview', sides: ['left', 'right'] },
  flows: { anchor: '.sb-flows__list', sides: ['right', 'left', 'below'] },
};

interface CardSpot { key: string; left: number; top: number; }

/**
 * Picks the card's top-left in stage space: the kept spot if it is still clear, else the first clear spot beside the
 * anchor (in side order, start- then end-aligned), else an area corner, else whichever spot covers the least.
 */
export function placeCard(card: { width: number; height: number }, area: FocusBox, anchor: FocusBox | null, avoid: FocusBox[],
  sides: BeatSide[], keep = ''): CardSpot {
  const gap = BEAT_MARGIN, w = card.width, h = card.height;
  const x0 = area.left + gap, x1 = area.right - gap - w, y0 = area.top + gap, y1 = area.bottom - gap - h;
  const clampX = (x: number): number => Math.max(x0, Math.min(x, x1)), clampY = (y: number): number => Math.max(y0, Math.min(y, y1));
  const spots: CardSpot[] = [];
  for (const side of anchor ? sides : []) {
    if (side === 'left' || side === 'right') {
      const left = side === 'left' ? anchor.left - gap - w : anchor.right + gap;
      spots.push({ key: side, left, top: clampY(anchor.top) }, { key: `${side}-end`, left, top: clampY(anchor.bottom - h) });
    } else {
      const top = side === 'above' ? anchor.top - gap - h : anchor.bottom + gap;
      spots.push({ key: side, left: clampX(anchor.left), top }, { key: `${side}-end`, left: clampX(anchor.right - w), top });
    }
  }
  spots.push({ key: 'top-left', left: x0, top: y0 }, { key: 'top-right', left: x1, top: y0 },
    { key: 'bottom-left', left: x0, top: y1 }, { key: 'bottom-right', left: x1, top: y1 });
  const blocked = anchor ? [anchor, ...avoid] : avoid;
  const overlap = (spot: CardSpot): number => blocked.reduce((sum, box) => sum
    + Math.max(0, Math.min(spot.left + w, box.right) - Math.max(spot.left, box.left))
    * Math.max(0, Math.min(spot.top + h, box.bottom) - Math.max(spot.top, box.top)), 0);
  const fits = (spot: CardSpot): boolean => spot.left >= x0 - 0.5 && spot.left <= x1 + 0.5 && spot.top >= y0 - 0.5
    && spot.top <= y1 + 0.5 && !overlap(spot);
  const kept = spots.find(spot => spot.key === keep);
  const clear = kept && fits(kept) ? kept : spots.find(fits);
  if (clear) { return clear; }
  // Nothing is fully clear (small stage): stay in the area and cover as little of the active region as possible.
  return spots.map(spot => ({ key: spot.key, left: clampX(spot.left), top: clampY(spot.top) }))
    .reduce((best, spot) => overlap(spot) < overlap(best) ? spot : best);
}

/** Reading pause before the cursor moves: about 140ms a word, held between 1 and 2.4 seconds. */
export function readMs(title: string, body: string): number {
  const words = `${title} ${body}`.trim().split(/\s+/).length;
  return Math.min(2400, Math.max(1000, words * 140));
}

/**
 * Alpha mask for the scrim: opaque everywhere except a Gaussian-feathered rounded hole. Masking (not clipping) the
 * scrim feathers its dim and its backdrop blur together. The blurred rect is grown by 2σ so the padded box stays clear.
 */
export function focusMask(box: FocusBox, width: number, height: number): string {
  const round = (value: number): number => Math.round(value * 2) / 2, grow = FEATHER * 2;
  const x = round(box.left - grow), y = round(box.top - grow);
  const w = round(box.right - box.left + grow * 2), h = round(box.bottom - box.top + grow * 2);
  const r = Math.min(FOCUS_RADIUS + grow, w / 2, h / 2);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">`
    + `<filter id="f" filterUnits="userSpaceOnUse" x="0" y="0" width="${width}" height="${height}">`
    + `<feGaussianBlur stdDeviation="${FEATHER}"/></filter>`
    + `<mask id="m"><rect width="100%" height="100%" fill="#fff"/><rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${r}" filter="url(#f)"/></mask>`
    + '<rect width="100%" height="100%" mask="url(#m)"/></svg>';
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

/** The demo flow was started for the prepared PRACTICE_AI course and module. */
const DEMO_TARGET: ScenarioTarget = {
  courseId: PRACTICE_AI.courses.courses[0].id, courseName: PRACTICE_AI.courses.courses[0].name,
  moduleId: PRACTICE_AI.courses.modules[0].id, moduleName: PRACTICE_AI.courses.modules[0].label,
};

/** Fixed checkpoints make chapter navigation immediate without replaying earlier clicks. */
function checkpoint(chapter: number): Scenario {
  const scenario: Scenario = { ...JSON.parse(JSON.stringify(PRACTICE_SCENARIO)), target: { ...DEMO_TARGET } };
  attachScenarioCoaching(scenario);
  if (chapter === 0) { return { title: 'Untitled scenario', subtitle: '', steps: [], target: { ...DEMO_TARGET } }; }
  if (chapter === 1) { scenario.steps = scenario.steps.slice(0, 1); scenario.steps[0].next = undefined; }
  if (chapter === 2) {
    scenario.steps = scenario.steps.slice(0, 2);
    scenario.steps[1].coaching = undefined;
    scenario.steps[1].next = undefined;
    scenario.steps[1].onCorrect = undefined; scenario.steps[1].onIncorrect = undefined;
  }
  if (chapter === 3) {
    scenario.steps[1].onCorrect = undefined; scenario.steps[1].onIncorrect = undefined;
    scenario.steps[1].next = undefined;
  }
  return scenario;
}
